import { t } from 'i18next';
import { useGetSubscribedReportIds, useUpdateReportSubscription } from '../api/notifications/notifications';
import { useFeedbackMessages } from './useFeedbackMessages';

export const useReportSubscriptions = (isSubscribeToReportEnabled: boolean | undefined) => {
    const {
        data: subscribedReportIdsResponse,
        isLoading: isSubscribedReportIdsLoading,
        isFetching: isSubscribedReportIdsFetching,
        refetch: refetchSubscribedReportIds,
    } = useGetSubscribedReportIds({ query: { enabled: !!isSubscribeToReportEnabled } });
    const { mutateAsync: updateReportSubscription, isLoading: isUpdatingSubscription } = useUpdateReportSubscription();
    const { displayFeedbackMessage } = useFeedbackMessages();

    if (!isSubscribeToReportEnabled) {
        return {
            isLoadingOrUpdatingReportSubscriptions: false,
            checkIsUserSubscribedToReport: () => false,
            toggleReportSubscription: () => {},
        };
    }

    const isLoadingOrUpdatingReportSubscriptions = isSubscribedReportIdsLoading || isSubscribedReportIdsFetching || isUpdatingSubscription;
    const subscribedReportIdsSet = new Set(subscribedReportIdsResponse?.subscribedReportIds);

    const checkIsUserSubscribedToReport = (reportId: number) => subscribedReportIdsSet.has(reportId.toString());

    const toggleReportSubscription = (reportId: number) => {
        const isCurrentlySubscribed = checkIsUserSubscribedToReport(reportId);

        updateReportSubscription(
            { data: { reportId: reportId.toString(), isSubscribed: !isCurrentlySubscribed } },
            {
                onSuccess: async () => {
                    refetchSubscribedReportIds();
                    displayFeedbackMessage({
                        type: 'success',
                        message: t(isCurrentlySubscribed ? 'unsubscribeFromReportSuccess' : 'subscribeToReportSuccess'),
                    });
                },
                onError: () => {
                    displayFeedbackMessage({
                        type: 'error',
                        message: t(isCurrentlySubscribed ? 'unsubscribeFromReportError' : 'subscribeToReportError'),
                    });
                },
            }
        );
    };

    return { isLoadingOrUpdatingReportSubscriptions, checkIsUserSubscribedToReport, toggleReportSubscription };
};
